import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { resolve, t, tEnum } from 'app/core/i18n/i18n';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';
import {
  BP_GROUPING_ICONS,
  BP_GROUPING_LABEL_KEYS,
  BP_GROUPING_SECTIONS,
  BP_GROUPING_SHORT_LABEL_KEYS,
  BP_GROUPING_TOOLTIP_KEYS,
} from '../../constants/bp-grouping.constants';
import { BPGroupingCriteria, BPGroupingSection } from '../../types/bp-grouping.types';

@Component({
  selector: 'app-bp-grouping-selector',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    MatDividerModule,
    TranslatePipe,
  ],
  templateUrl: './bp-grouping-selector.component.html',
  styleUrls: ['./bp-grouping-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BpGroupingSelectorComponent),
      multi: true,
    },
  ],
})
export class BpGroupingSelectorComponent implements ControlValueAccessor {
  // Expose constants to template
  readonly BPGroupingCriteria = BPGroupingCriteria;
  readonly BP_GROUPING_ICONS = BP_GROUPING_ICONS;
  readonly BP_GROUPING_SECTIONS = BP_GROUPING_SECTIONS;

  /** Resolves a grouping option's label at render time — never cache the result. */
  getGroupingLabel(option: BPGroupingCriteria): string {
    return tEnum(BP_GROUPING_LABEL_KEYS, option) ?? '';
  }

  /** Resolves a menu section's header at render time — `name` is a compile-checked TranslationKey. */
  getSectionName(section: BPGroupingSection): string {
    return resolve(section.name);
  }

  // Internal value
  private _value: BPGroupingCriteria = BPGroupingCriteria.NONE;

  /** Input for direct property binding */
  @Input()
  get value(): BPGroupingCriteria {
    return this._value;
  }
  set value(val: BPGroupingCriteria) {
    this._value = val || BPGroupingCriteria.NONE;
  }

  /** Event emitter for value changes (two-way binding support) */
  @Output() valueChange = new EventEmitter<BPGroupingCriteria>();

  // ControlValueAccessor callbacks
  private onChange: (value: BPGroupingCriteria) => void = () => {};
  private onTouched: () => void = () => {};

  // ControlValueAccessor implementation
  writeValue(val: BPGroupingCriteria): void {
    this._value = val || BPGroupingCriteria.NONE;
  }

  registerOnChange(fn: (value: BPGroupingCriteria) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Handle disabled state if needed
  }

  /** Handle grouping selection */
  selectGrouping(criteria: BPGroupingCriteria): void {
    this._value = criteria;
    this.onChange(criteria);
    this.onTouched();
    this.valueChange.emit(criteria);
  }

  /** Get icon for current value */
  get activeIcon(): string {
    return BP_GROUPING_ICONS[this._value];
  }

  /** Get tooltip text for current value */
  get tooltipText(): string {
    return tEnum(BP_GROUPING_TOOLTIP_KEYS, this._value) ?? '';
  }

  /** Get button text */
  get buttonText(): string {
    if (this._value === BPGroupingCriteria.NONE) {
      return t('routing.SUB_ROUTE_PARTNERS.GROUPING.BUTTON_DEFAULT_LABEL');
    }
    return t('routing.SUB_ROUTE_PARTNERS.GROUPING.BUTTON_PREFIX', {
      value: tEnum(BP_GROUPING_SHORT_LABEL_KEYS, this._value) ?? '',
    });
  }

  /** Check if grouping is active */
  get isGrouped(): boolean {
    return this._value !== BPGroupingCriteria.NONE;
  }

  /** Show header only when no grouping is selected */
  get showMenuHeader(): boolean {
    return this._value === BPGroupingCriteria.NONE;
  }
}
